import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  Inject,
  LOCALE_ID,
  OnInit,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import {
  NgxDatatableConfig,
  NgxDatatableColumn,
} from 'src/app/core/interfaces/ui/ngx-datatable.interface';
import {
  MOBILE_CARD_NESTED_LABEL_KEYS,
  MOBILE_CARD_NESTED_LABEL_MAX_DEPTH,
} from 'src/app/core/helpers/ui/ngx-datatable-mobile.constant';
import { MobileCardAction } from 'src/app/core/interfaces/ui/ngx-datatable-mobile.interface';
import { NgxDatatableMobileService } from 'src/app/core/services/ui/ngx-datatable-mobile.service';

@Component({
  selector: 'shared-tables-ngx-datatable-mobile-cards',
  templateUrl: './ngx-datatable-mobile-cards.component.html',
  styleUrls: ['./ngx-datatable-mobile-cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe],
})
export class NgxDatatableMobileCardsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input({ required: true }) data: any[] = [];
  @Input({ required: true }) config: NgxDatatableConfig | null = null;
  @Input() actions: MobileCardAction[] = [];
  @Input() cardTitleField: string = '';
  @Input() cardSubtitleField: string = '';
  @Input() isLoading: boolean = false;

  @Output() public onSelectRow = new EventEmitter<any>();
  @Output() public onChangeLimit = new EventEmitter<number>();
  @Output() public onChangePage = new EventEmitter<number>();
  @Output() public onButtonAction = new EventEmitter<{
    action: string;
    row: any;
  }>();

  public expandedCards: Set<any> = new Set();
  public maxVisibleFields: number = 4;

  public isOffcanvasOpen: boolean = false;
  public offcanvasContent: any = null;
  public offcanvasRow: any = null;

  private isDragging: boolean = false;
  private startY: number = 0;
  private startHeight: number = 0;

  private boundTouchStart?: (event: TouchEvent) => void;
  private boundTouchMove?: (event: TouchEvent) => void;
  private boundTouchEnd?: (event: TouchEvent) => void;
  private boundMouseDown?: (event: MouseEvent) => void;
  private boundMouseMove?: (event: MouseEvent) => void;
  private boundMouseUp?: (event: MouseEvent) => void;

  constructor(
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private mobileService: NgxDatatableMobileService,
    private translateService: TranslateService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  ngOnInit(): void {
    this.syncWithConfig();
  }

  ngOnChanges(): void {
    this.syncWithConfig();
  }

  ngOnDestroy(): void {
    this.removeOffcanvasListeners();
  }

  public get autoDetectedActions(): MobileCardAction[] {
    if (this.actions?.length > 0 || !this.config?.columns) return [];

    const detectionResult = this.mobileService.detectActionColumns(
      this.config.columns
    );

    return detectionResult.columns.length === 0 ||
      detectionResult.confidence < 0.5
      ? []
      : this.generateActionsFromColumns(detectionResult.columns);
  }

  public get actionColumns(): Partial<NgxDatatableColumn>[] {
    if (!this.config?.columns) return [];

    const detectionResult = this.mobileService.detectActionColumns(
      this.config.columns,
      {
        enableAutoDetection: true,
        positionBased: true,
        templateRequired: false,
        confidenceThreshold: 0.3,
      }
    );

    return detectionResult.columns.filter(
      (column) => !column.prop || column.prop === 'actions'
    );
  }

  public get mobileTitleColumn(): Partial<NgxDatatableColumn> | undefined {
    this.config?.columns?.forEach((col, index) => {});

    const result = this.config?.columns?.find((col) => {
      return col.isMobileTitle === true;
    });

    return result;
  }

  public get mobileSubtitleColumn(): Partial<NgxDatatableColumn> | undefined {
    this.config?.columns?.forEach((col, index) => {});

    const result = this.config?.columns?.find((col) => {
      return col.isMobileSubtitle === true;
    });

    return result;
  }

  public get expandableColumns(): Partial<NgxDatatableColumn>[] {
    if (!this.config?.columns) return [];

    const result = this.mobileService.detectExpandableContent(
      this.config.columns,
      undefined,
      {
        enableAutoDetection: true,
        checkRowData: false,
        confidenceThreshold: 0.3,
      }
    );

    return result.expandableColumns;
  }

  public hasExpandableContent(row: any): boolean {
    if (row.isTotalRow) return false;

    return this.mobileService.hasExpandableContent(
      this.config?.columns || [],
      row,
      {
        enableAutoDetection: true,
        checkRowData: true,
        confidenceThreshold: 0.3,
      }
    );
  }

  public openExpandableOffcanvas(row: any): void {
    const expandableColumn = this.expandableColumns[0];
    if (expandableColumn) {
      this.offcanvasRow = row;
      this.offcanvasContent = expandableColumn;
      this.isOffcanvasOpen = true;

      document.body.classList.add('overflow-hidden');

      this.cdr.detectChanges();

      setTimeout(() => {
        this.convertTablesToCards();
        this.setupOffcanvasResize();

        const offcanvas = document.getElementById('expandableOffcanvas');
        if (offcanvas) {
          setTimeout(() => {
            const maxContentHeight = this.getMaxContentHeight();
            const minHeight = window.innerHeight * 0.3;

            const optimalHeight = Math.max(maxContentHeight, minHeight);

            offcanvas.style.setProperty(
              'height',
              `${optimalHeight}px`,
              'important'
            );
            offcanvas.style.setProperty(
              'min-height',
              `${optimalHeight}px`,
              'important'
            );
            offcanvas.style.setProperty(
              'max-height',
              `${optimalHeight}px`,
              'important'
            );
          }, 100);
        }
      }, 150);
    }
  }

  public closeOffcanvas(): void {
    this.removeBodyScrollLock();

    this.isOffcanvasOpen = false;
    this.offcanvasContent = null;
    this.offcanvasRow = null;

    this.removeOffcanvasListeners();
    this.cdr.detectChanges();
  }

  private removeBodyScrollLock(): void {
    if (this.isOffcanvasOpen) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  private setupOffcanvasResize(): void {
    const offcanvas = document.getElementById('expandableOffcanvas');
    const resizeHandle = offcanvas?.querySelector(
      '.resize-handle'
    ) as HTMLElement;

    if (resizeHandle) {
      this.boundTouchStart = this.onTouchStart.bind(this);
      this.boundTouchMove = this.onTouchMove.bind(this);
      this.boundTouchEnd = this.onTouchEnd.bind(this);
      this.boundMouseDown = this.onMouseDown.bind(this);
      this.boundMouseMove = this.onMouseMove.bind(this);
      this.boundMouseUp = this.onMouseUp.bind(this);

      resizeHandle.addEventListener('touchstart', this.boundTouchStart, {
        passive: false,
      });

      resizeHandle.addEventListener('mousedown', this.boundMouseDown);
    }
  }

  private removeOffcanvasListeners(): void {
    const offcanvas = document.getElementById('expandableOffcanvas');
    const resizeHandle = offcanvas?.querySelector(
      '.resize-handle'
    ) as HTMLElement;

    if (resizeHandle && this.boundTouchStart && this.boundMouseDown) {
      resizeHandle.removeEventListener('touchstart', this.boundTouchStart);

      resizeHandle.removeEventListener('mousedown', this.boundMouseDown);
    }

    if (this.boundTouchMove) {
      document.removeEventListener('touchmove', this.boundTouchMove);
    }
    if (this.boundTouchEnd) {
      document.removeEventListener('touchend', this.boundTouchEnd);
    }
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove);
    }
    if (this.boundMouseUp) {
      document.removeEventListener('mouseup', this.boundMouseUp);
    }

    this.boundTouchStart = undefined;
    this.boundTouchMove = undefined;
    this.boundTouchEnd = undefined;
    this.boundMouseDown = undefined;
    this.boundMouseMove = undefined;
    this.boundMouseUp = undefined;

    this.isDragging = false;
  }

  private getMaxContentHeight(): number {
    const offcanvas = document.getElementById('expandableOffcanvas');
    if (!offcanvas) return window.innerHeight * 0.9;

    const header = offcanvas.querySelector('.offcanvas-header') as HTMLElement;
    const body = offcanvas.querySelector('.offcanvas-body') as HTMLElement;

    if (!body) return window.innerHeight * 0.9;

    const headerHeight = header ? header.offsetHeight : 70;

    const bodyContentHeight = this.calculateBodyContentHeight(body);

    const padding = 40;

    const maxHeight = headerHeight + bodyContentHeight + padding;

    return Math.min(maxHeight, window.innerHeight * 0.9);
  }

  private calculateBodyContentHeight(body: HTMLElement): number {
    const clone = body.cloneNode(true) as HTMLElement;

    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.overflowY = 'visible';
    clone.style.width = body.offsetWidth + 'px';

    document.body.appendChild(clone);

    const naturalHeight = clone.scrollHeight;

    document.body.removeChild(clone);

    return naturalHeight;
  }

  private onTouchStart(event: TouchEvent): void {
    const target = event.target as HTMLElement;

    if (
      target &&
      (target.closest('.btn-close') ||
        target.closest('button') ||
        target.classList.contains('btn-close'))
    ) {
      return;
    }

    this.isDragging = true;
    this.startY = event.touches[0].clientY;
    const offcanvas = document.getElementById('expandableOffcanvas');
    if (offcanvas) {
      this.startHeight =
        parseInt(getComputedStyle(offcanvas).height) || offcanvas.offsetHeight;
      offcanvas.classList.add('resizing');
    }

    document.addEventListener('touchmove', this.boundTouchMove!, {
      passive: false,
    });
    document.addEventListener('touchend', this.boundTouchEnd!, {
      passive: false,
    });

    event.preventDefault();
    event.stopPropagation();
  }

  private onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;

    const currentY = event.touches[0].clientY;
    const deltaY = this.startY - currentY;

    const maxContentHeight = this.getMaxContentHeight();

    const newHeight = Math.min(
      Math.max(this.startHeight + deltaY, window.innerHeight * 0.3),
      Math.min(window.innerHeight * 0.9, maxContentHeight)
    );

    const offcanvas = document.getElementById('expandableOffcanvas');
    if (offcanvas) {
      offcanvas.style.setProperty('height', `${newHeight}px`, 'important');
      offcanvas.style.setProperty('min-height', `${newHeight}px`, 'important');
      offcanvas.style.setProperty('max-height', `${newHeight}px`, 'important');
    }
    event.preventDefault();
    event.stopPropagation();
  }

  private onTouchEnd(event: TouchEvent): void {
    this.isDragging = false;
    const offcanvas = document.getElementById('expandableOffcanvas');
    if (offcanvas) {
      offcanvas.classList.remove('resizing');
    }

    document.removeEventListener('touchmove', this.boundTouchMove!);
    document.removeEventListener('touchend', this.boundTouchEnd!);

    event.preventDefault();
    event.stopPropagation();
  }

  private onMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (
      target &&
      (target.closest('.btn-close') ||
        target.closest('button') ||
        target.classList.contains('btn-close'))
    ) {
      return;
    }

    this.isDragging = true;
    this.startY = event.clientY;
    const offcanvas = document.getElementById('expandableOffcanvas');
    if (offcanvas) {
      this.startHeight =
        parseInt(getComputedStyle(offcanvas).height) || offcanvas.offsetHeight;
      offcanvas.classList.add('resizing');
    }

    document.addEventListener('mousemove', this.boundMouseMove!);
    document.addEventListener('mouseup', this.boundMouseUp!);

    event.preventDefault();
    event.stopPropagation();
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    const currentY = event.clientY;
    const deltaY = this.startY - currentY;

    const maxContentHeight = this.getMaxContentHeight();

    const newHeight = Math.min(
      Math.max(this.startHeight + deltaY, window.innerHeight * 0.3),
      Math.min(window.innerHeight * 0.9, maxContentHeight)
    );

    const offcanvas = document.getElementById('expandableOffcanvas');
    if (offcanvas) {
      offcanvas.style.setProperty('height', `${newHeight}px`, 'important');
      offcanvas.style.setProperty('min-height', `${newHeight}px`, 'important');
      offcanvas.style.setProperty('max-height', `${newHeight}px`, 'important');
    }
    event.preventDefault();
    event.stopPropagation();
  }

  private onMouseUp(event: MouseEvent): void {
    this.isDragging = false;
    const offcanvas = document.getElementById('expandableOffcanvas');
    if (offcanvas) {
      offcanvas.classList.remove('resizing');
    }

    document.removeEventListener('mousemove', this.boundMouseMove!);
    document.removeEventListener('mouseup', this.boundMouseUp!);

    event.preventDefault();
    event.stopPropagation();
  }

  public get allActions(): MobileCardAction[] {
    const manualActions = this.actions || [];
    const autoActions =
      manualActions.length > 0 ? [] : this.autoDetectedActions;
    return [...manualActions, ...autoActions];
  }

  private generateActionsFromColumns(
    columns: Partial<NgxDatatableColumn>[]
  ): MobileCardAction[] {
    return [];
  }

  private syncWithConfig(): void {
    if (this.config) {
      this.cdr.detectChanges();
    }
  }

  public get visibleColumns(): Partial<NgxDatatableColumn>[] {
    if (!this.config?.columns) return [];

    const result = this.mobileService.detectVisibleColumns(
      this.config.columns,
      {
        enableAutoDetection: true,
        respectVisibleProperty: true,
        excludeEmptyColumns: true,
        confidenceThreshold: 0.3,
        customExcludePatterns: ['actions', 'acciones', 'expand', 'detail'],
      }
    );

    return result.visibleColumns;
  }

  public get paginatedData(): any[] {
    return this.data || [];
  }

  public get totalPages(): number {
    if (!this.config?.count || !this.config?.limit) {
      return Math.ceil((this.data?.length || 0) / (this.config?.limit || 10));
    }
    return Math.ceil(this.config.count / this.config.limit);
  }

  public get currentServerPage(): number {
    return this.config?.page || 1;
  }

  public get currentServerLimit(): number {
    return this.config?.limit || 10;
  }

  public get pages(): (number | string)[] {
    return [];
  }

  public getVisiblePages(): number[] {
    const current = this.currentServerPage;
    const total = this.totalPages;
    const visiblePages: number[] = [];

    if (total <= 5) {
      for (let i = 1; i <= total; i++) {
        visiblePages.push(i);
      }
    } else {
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);

      if (end - start < 4) {
        if (start === 1) {
          end = Math.min(total, start + 4);
        } else if (end === total) {
          start = Math.max(1, end - 4);
        }
      }

      for (let i = start; i <= end; i++) {
        visiblePages.push(i);
      }
    }

    return visiblePages;
  }

  public getCardTitle(row: any): string {
    const titleFromMobileColumn = this.getTitleFromMobileColumn(row);
    if (titleFromMobileColumn) {
      return titleFromMobileColumn;
    }

    const titleFromSpecificField = this.getTitleFromSpecificField(row);
    if (titleFromSpecificField) {
      return titleFromSpecificField;
    }

    const titleFromVisibleColumns = this.getTitleFromVisibleColumns(row);
    if (titleFromVisibleColumns) {
      return titleFromVisibleColumns;
    }

    const titleFromObjectKeys = this.getTitleFromObjectKeys(row);
    if (titleFromObjectKeys) {
      return titleFromObjectKeys;
    }

    return this.getDefaultTitle();
  }

  private getTitleFromMobileColumn(row: any): string | null {
    const col = this.mobileTitleColumn;
    if (col?.prop) {
      const display = this.displayString(this.getNestedValue(row, col.prop));
      if (display) {
        return display;
      }
    }
    return null;
  }

  private getTitleFromSpecificField(row: any): string | null {
    if (!this.cardTitleField) {
      return null;
    }

    const directValue = this.getValueFromDirectField(row, this.cardTitleField);
    if (directValue) {
      return directValue;
    }

    const variationValue = this.getValueFromFieldVariations(
      row,
      this.cardTitleField
    );
    if (variationValue) {
      return variationValue;
    }

    return null;
  }

  private getValueFromDirectField(row: any, fieldName: string): string | null {
    const value = this.getNestedValue(row, fieldName) || row[fieldName];
    return this.displayString(value);
  }

  private getValueFromFieldVariations(
    row: any,
    fieldName: string
  ): string | null {
    const fieldVariations = this.generateFieldVariations(fieldName);
    for (const variation of fieldVariations) {
      const display = this.displayString(
        this.getNestedValue(row, variation) || row[variation]
      );
      if (display) {
        return display;
      }
    }
    return null;
  }

  private getTitleFromVisibleColumns(row: any): string | null {
    for (const column of this.visibleColumns) {
      if (column.prop && column.prop !== 'actions') {
        const display = this.displayString(this.getNestedValue(row, column.prop));
        if (display) {
          return display;
        }
      }
    }
    return null;
  }

  private getTitleFromObjectKeys(row: any): string | null {
    const keys = Object.keys(row || {});
    for (const key of keys.slice(0, 3)) {
      if (key === 'actions') {
        continue;
      }
      const display = this.displayString(row[key]);
      if (display) {
        return display;
      }
    }
    return null;
  }

  private displayString(
    value: any,
    depth: number = MOBILE_CARD_NESTED_LABEL_MAX_DEPTH
  ): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    if (value instanceof Date) {
      return String(value);
    }
    if (Array.isArray(value) || depth <= 0) {
      return null;
    }

    for (const key of MOBILE_CARD_NESTED_LABEL_KEYS) {
      const inner = (value as Record<string, unknown>)[key];
      if (inner === null || inner === undefined || inner === '') continue;
      if (typeof inner === 'object') continue;
      return String(inner);
    }

    for (const inner of Object.values(value as Record<string, unknown>)) {
      if (inner === null || typeof inner !== 'object' || Array.isArray(inner)) continue;
      const nested = this.displayString(inner, depth - 1);
      if (nested) {
        return nested;
      }
    }

    return null;
  }

  private isValidValue(value: any): boolean {
    return this.displayString(value) !== null;
  }

  private getDefaultTitle(): string {
    return 'N/A';
  }

  public getCardSubtitleColor(row: any): string | null {
    const colorProp = this.mobileSubtitleColumn?.mobileSubtitleColorProp;
    if (!colorProp) return null;

    const color = this.getNestedValue(row, colorProp);
    return this.isValidValue(color) ? String(color) : null;
  }

  public getCardSubtitle(row: any): string {
    const subtitleFromMobileColumn = this.getSubtitleFromMobileColumn(row);
    if (subtitleFromMobileColumn) {
      return subtitleFromMobileColumn;
    }

    const subtitleFromSpecificField = this.getSubtitleFromSpecificField(row);
    if (subtitleFromSpecificField) {
      return subtitleFromSpecificField;
    }

    const subtitleFromVisibleColumns = this.getSubtitleFromVisibleColumns(row);
    if (subtitleFromVisibleColumns) {
      return subtitleFromVisibleColumns;
    }

    const subtitleFromObjectKeys = this.getSubtitleFromObjectKeys(row);
    if (subtitleFromObjectKeys) {
      return subtitleFromObjectKeys;
    }

    return this.getDefaultSubtitle();
  }

  private getSubtitleFromMobileColumn(row: any): string | null {
    const col = this.mobileSubtitleColumn;
    if (col?.prop) {
      const value = this.getNestedValue(row, col.prop);
      if (this.isValidValue(value)) {
        return String(value);
      }
    }
    return null;
  }

  private getSubtitleFromSpecificField(row: any): string | null {
    if (!this.cardSubtitleField) {
      return null;
    }

    const directValue = this.getValueFromDirectField(
      row,
      this.cardSubtitleField
    );
    if (directValue) {
      return directValue;
    }

    const variationValue = this.getValueFromFieldVariations(
      row,
      this.cardSubtitleField
    );
    if (variationValue) {
      return variationValue;
    }

    return null;
  }

  private getSubtitleFromVisibleColumns(row: any): string | null {
    const titleColumn = this.getCardTitleColumnName();
    let skipFirst = true;

    for (const column of this.visibleColumns) {
      if (column.prop && column.prop !== 'actions') {
        if (skipFirst) {
          skipFirst = false;
          continue;
        }

        const value = this.getNestedValue(row, column.prop);
        if (this.isValidValue(value)) {
          return String(value);
        }
      }
    }
    return null;
  }

  private getSubtitleFromObjectKeys(row: any): string | null {
    const keys = Object.keys(row || {});
    const titleValue = this.getCardTitle(row);
    for (const key of keys.slice(0, 5)) {
      const value = row[key];
      if (
        this.isValidValue(value) &&
        key !== 'actions' &&
        String(value) !== titleValue
      ) {
        return String(value);
      }
    }
    return null;
  }

  private getDefaultSubtitle(): string {
    return this.translateService.instant('COMMON.NO_SUBTITLE') || '';
  }

  public getCardTitleColumnName(): string {
    if (this.mobileTitleColumn?.name) {
      return this.mobileTitleColumn.name;
    }
    if (this.mobileTitleColumn?.prop) {
      return this.mobileTitleColumn.prop;
    }

    if (this.cardTitleField) {
      return this.cardTitleField;
    }

    const firstVisibleColumn = this.visibleColumns.find(
      (col) => col.prop && col.prop !== 'actions'
    );
    if (firstVisibleColumn?.name) {
      return firstVisibleColumn.name;
    }
    if (firstVisibleColumn?.prop) {
      return firstVisibleColumn.prop;
    }

    const result =
      this.visibleColumns[0]?.name || this.visibleColumns[0]?.prop || '';
    return result;
  }

  public getCardSubtitleColumnName(): string {
    if (this.mobileSubtitleColumn?.name) {
      return this.mobileSubtitleColumn.name;
    }
    if (this.mobileSubtitleColumn?.prop) {
      return this.mobileSubtitleColumn.prop;
    }

    if (this.cardSubtitleField) {
      return this.cardSubtitleField;
    }

    const titleColumnName = this.getCardTitleColumnName();
    const subtitleColumn = this.visibleColumns.find(
      (col) =>
        col.prop &&
        col.prop !== 'actions' &&
        col.prop !== titleColumnName &&
        col.name !== titleColumnName
    );
    if (subtitleColumn?.name) {
      return subtitleColumn.name;
    }
    if (subtitleColumn?.prop) {
      return subtitleColumn.prop;
    }

    const result =
      this.visibleColumns[1]?.name || this.visibleColumns[1]?.prop || '';
    return result;
  }

  public getFieldValue(
    row: any,
    column: string | Partial<NgxDatatableColumn>
  ): any {
    if (!row) {
      return 'N/A';
    }

    const columnName = this.resolveColumnName(column);
    if (!columnName) {
      return 'N/A';
    }

    if (this.shouldReturnNullForTemplate(column)) {
      return null;
    }

    const value = this.searchValueInRow(row, columnName);
    const columnObj = typeof column === 'object' ? column : { prop: column };
    return this.formatValueByType(value, columnObj);
  }

  private resolveColumnName(
    column: string | Partial<NgxDatatableColumn>
  ): string | null {
    if (typeof column === 'string') {
      return column;
    }

    if (column && (column.prop || column.name)) {
      return column.prop || column.name || '';
    }

    return null;
  }

  private shouldReturnNullForTemplate(
    column: string | Partial<NgxDatatableColumn>
  ): boolean {
    return typeof column === 'object' && !!column.cellTemplate;
  }

  private searchValueInRow(row: any, columnName: string): any {
    const directValue = this.getDirectValue(row, columnName);
    if (directValue !== null) {
      return directValue;
    }

    const nestedValue = this.getNestedValue(row, columnName);
    if (nestedValue !== null && nestedValue !== undefined) {
      return nestedValue;
    }

    const fieldVariationValue = this.searchInFieldVariations(row, columnName);
    if (fieldVariationValue !== null) {
      return fieldVariationValue;
    }

    return this.searchInColumnVariations(row, columnName);
  }

  private getDirectValue(row: any, columnName: string): any {
    return row.hasOwnProperty(columnName) ? row[columnName] : null;
  }

  private searchInFieldVariations(row: any, columnName: string): any {
    const fieldVariations = this.generateFieldVariations(columnName);
    for (const variation of fieldVariations) {
      if (row.hasOwnProperty(variation)) {
        return row[variation];
      }
      const nestedVariationValue = this.getNestedValue(row, variation);
      if (nestedVariationValue !== null && nestedVariationValue !== undefined) {
        return nestedVariationValue;
      }
    }
    return null;
  }

  private searchInColumnVariations(row: any, columnName: string): any {
    const columnVariations = this.generateColumnNameVariations(columnName);
    for (const variation of columnVariations) {
      if (row.hasOwnProperty(variation)) {
        return row[variation];
      }
      const nestedVariationValue = this.getNestedValue(row, variation);
      if (nestedVariationValue !== null && nestedVariationValue !== undefined) {
        return nestedVariationValue;
      }
    }
    return null;
  }

  public hasTemplate(column: Partial<NgxDatatableColumn>): boolean {
    return !!column.cellTemplate;
  }

  private formatValueByType(
    value: any,
    column: Partial<NgxDatatableColumn>
  ): any {
    if (
      value == null ||
      value === undefined ||
      value === '' ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      return 'N/A';
    }

    if (this.isDateValue(value)) {
      return this.formatDate(value);
    }

    if (this.isIPAddress(value)) {
      return value.toString();
    }

    if (this.isNumericValue(value)) {
      if (this.isIntegerValue(value)) {
        return value.toString();
      }
      return this.formatNumber(value);
    }

    return value;
  }

  private isIntegerValue(value: any): boolean {
    const num = parseFloat(value);
    return !isNaN(num) && Number.isInteger(num);
  }

  private isDateValue(value: any): boolean {
    if (!value) return false;

    const date = new Date(value);
    return (
      !isNaN(date.getTime()) &&
      ((typeof value === 'string' &&
        (value.includes('-') || value.includes('/') || value.includes('T'))) ||
        value instanceof Date ||
        (typeof value === 'number' && value > 1000000000))
    );
  }

  private isIPAddress(value: any): boolean {
    if (typeof value !== 'string') return false;

    const ipv4Pattern =
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

    const ipv6Pattern = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$/;

    return ipv4Pattern.test(value) || ipv6Pattern.test(value);
  }

  private isNumericValue(value: any): boolean {
    if (value == null || value === '') return false;

    const num = parseFloat(value);
    return !isNaN(num) && isFinite(num) && typeof value !== 'boolean';
  }

  private formatDate(value: any): string {
    if (!value) return 'N/A';
    try {
      return (
        this.datePipe.transform(value, 'dd/MM/yyyy HH:mm:ss') ||
        value.toString()
      );
    } catch {
      return value.toString();
    }
  }

  private formatNumber(value: any): string {
    const num = parseFloat(value);
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString(this.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  private generateColumnNameVariations(columnName: string): string[] {
    const variations: string[] = [];
    const cleanName = columnName.toLowerCase();

    variations.push(columnName);

    variations.push(cleanName);

    variations.push(cleanName.replace(/\s+/g, ''));

    variations.push(cleanName.replace(/[^a-z]/g, ''));

    const camelCase = cleanName.replace(/\s+(.)/g, (_, char) =>
      char.toUpperCase()
    );
    variations.push(camelCase);

    const pascalCase = camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
    variations.push(pascalCase);

    variations.push(cleanName.replace(/\s+/g, '_'));

    variations.push(cleanName.replace(/\s+/g, '-'));

    return [...new Set(variations)];
  }

  private generateFieldVariations(fieldName: string): string[] {
    if (!fieldName) return [];

    const variations: string[] = [fieldName];
    const cleanField = fieldName.trim();

    variations.push(
      cleanField.toLowerCase(),
      cleanField.toUpperCase(),
      this.capitalizeFirst(cleanField),
      this.lowercaseFirst(cleanField)
    );

    variations.push(
      this.toSnakeCase(cleanField),
      this.toCamelCase(cleanField),
      this.toKebabCase(cleanField),
      this.toPascalCase(cleanField)
    );

    variations.push(
      this.removeSeparators(cleanField),
      this.replaceWithUnderscore(cleanField),
      this.replaceWithHyphen(cleanField),
      this.replaceWithSpace(cleanField)
    );

    variations.push(...this.generateCommonTransformations(cleanField));

    variations.push(...this.generateAlgorithmicVariations(cleanField));

    return this.cleanAndSortVariations(variations, fieldName);
  }

  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  private lowercaseFirst(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  private toSnakeCase(str: string): string {
    return str
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/^_/, '');
  }

  private toCamelCase(str: string): string {
    return str.replace(/[_-]([a-z])/g, (_, letter) => letter.toUpperCase());
  }

  private toKebabCase(str: string): string {
    return str
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '');
  }

  private toPascalCase(str: string): string {
    const camel = this.toCamelCase(str);
    return camel.charAt(0).toUpperCase() + camel.slice(1);
  }

  private removeSeparators(str: string): string {
    return str.replace(/[_\-\s]/g, '');
  }

  private replaceWithUnderscore(str: string): string {
    return str.replace(/[\-\s]/g, '_');
  }

  private replaceWithHyphen(str: string): string {
    return str.replace(/[_\s]/g, '-');
  }

  private replaceWithSpace(str: string): string {
    return str.replace(/[_\-]/g, ' ');
  }

  private generateCommonTransformations(field: string): string[] {
    const variations: string[] = [];
    const lower = field.toLowerCase();

    if (lower.endsWith('s') && lower.length > 2) {
      variations.push(field.slice(0, -1));
    } else {
      variations.push(field + 's');
    }

    const commonEndings = ['ed', 'ing', 'er', 'ly'];
    commonEndings.forEach((ending) => {
      if (lower.endsWith(ending)) {
        variations.push(field.slice(0, -ending.length));
      } else {
        variations.push(field + ending);
      }
    });

    return variations;
  }

  private generateAlgorithmicVariations(field: string): string[] {
    const variations: string[] = [];
    const lower = field.toLowerCase();

    if (field.length > 3) {
      variations.push(field.substring(0, 3));
      variations.push(field.substring(0, 4));

      const words = field.split(/[_\-\s]/);
      if (words.length > 1) {
        variations.push(words.map((w) => w.charAt(0)).join(''));
        variations.push(words.map((w) => w.charAt(0).toUpperCase()).join(''));
      }
    }

    ['1', '2', 'Id', 'ID', 'At', 'By', 'To', 'Of'].forEach((suffix) => {
      variations.push(field + suffix);
      if (field.endsWith(suffix)) {
        variations.push(field.slice(0, -suffix.length));
      }
    });

    if (lower.length > 2) {
      const firstTwo = field.substring(0, 2);
      const rest = field.substring(2);

      if (field.length > 3) {
        variations.push(firstTwo.charAt(1) + firstTwo.charAt(0) + rest);
      }

      variations.push(field.charAt(0) + field);
    }

    return variations;
  }

  private cleanAndSortVariations(
    variations: string[],
    originalField: string
  ): string[] {
    const cleanedVariations = this.cleanVariations(variations);
    return this.sortVariationsBySimilarity(cleanedVariations, originalField);
  }

  private cleanVariations(variations: string[]): string[] {
    return [
      ...new Set(
        variations.filter((variation) => this.isValidVariation(variation))
      ),
    ];
  }

  private isValidVariation(variation: string): boolean {
    return !!variation && variation.trim() !== '' && variation.length > 1;
  }

  private sortVariationsBySimilarity(
    variations: string[],
    originalField: string
  ): string[] {
    return variations.sort((a, b) => {
      return this.compareVariations(a, b, originalField);
    });
  }

  private compareVariations(
    variationA: string,
    variationB: string,
    originalField: string
  ): number {
    if (variationA === originalField) return -1;
    if (variationB === originalField) return 1;

    const similarityA = this.calculateSimilarity(variationA, originalField);
    const similarityB = this.calculateSimilarity(variationB, originalField);

    if (similarityA !== similarityB) {
      return similarityB - similarityA;
    }

    return this.compareBylength(variationA, variationB);
  }

  private compareBylength(stringA: string, stringB: string): number {
    return stringA.length - stringB.length;
  }

  private calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  public getVisibleActions(row: any): MobileCardAction[] {
    return this.allActions.filter(
      (action) => !action.visible || action.visible(row)
    );
  }

  public triggerButtonAction(row: any, action: string): void {
    this.onButtonAction.emit({ action, row });
  }

  public isActionDisabled(action: MobileCardAction, row: any): boolean {
    return action.disabled ? action.disabled(row) : false;
  }

  public executeAction(action: MobileCardAction, row: any): void {
    if (!this.isActionDisabled(action, row)) {
      action.action(row);
    }
  }

  public selectCard(row: any): void {
    this.onSelectRow.emit(row);
  }

  public changePage(page: number | string): void {
    if (typeof page === 'number' && page >= 1 && page <= this.totalPages) {
      this.onChangePage.emit(page);
    }
  }

  public isPageActive(page: number | string): boolean {
    return typeof page === 'number' && page === this.currentServerPage;
  }

  public isPageNumber(page: number | string): boolean {
    return typeof page === 'number';
  }

  public toggleCardExpansion(row: any): void {
    if (this.expandedCards.has(row)) {
      this.expandedCards.delete(row);
    } else {
      this.expandedCards.add(row);
    }
  }

  public isCardExpanded(row: any): boolean {
    return this.expandedCards.has(row);
  }

  public getVisibleFields(row: any): Partial<NgxDatatableColumn>[] {
    let fieldsToShow = [...this.visibleColumns];

    fieldsToShow = this.filterActionColumns(fieldsToShow);
    fieldsToShow = this.filterExpandableAndFrozenColumns(fieldsToShow);
    fieldsToShow = this.filterTitleAndSubtitleColumns(fieldsToShow, row);
    fieldsToShow = this.filterFirstColumnIfUsedAsTitle(fieldsToShow, row);

    return this.applyFieldLimit(fieldsToShow, row);
  }

  private filterActionColumns(
    fields: Partial<NgxDatatableColumn>[]
  ): Partial<NgxDatatableColumn>[] {
    const actionColumns = this.actionColumns;
    return fields.filter(
      (col) => !actionColumns.some((actionCol) => actionCol.name === col.name)
    );
  }

  private filterExpandableAndFrozenColumns(
    fields: Partial<NgxDatatableColumn>[]
  ): Partial<NgxDatatableColumn>[] {
    return fields.filter((col) => {
      return !(col.isExpandable || col.frozenLeft);
    });
  }

  private filterTitleAndSubtitleColumns(
    fields: Partial<NgxDatatableColumn>[],
    row: any
  ): Partial<NgxDatatableColumn>[] {
    let filteredFields = fields;

    if (this.cardTitleField) {
      filteredFields = filteredFields.filter(
        (col) => col.prop !== this.cardTitleField
      );
    }

    if (this.cardSubtitleField) {
      filteredFields = filteredFields.filter(
        (col) => col.prop !== this.cardSubtitleField
      );
    }

    filteredFields = filteredFields.filter(
      (col) => !col.isMobileTitle && !col.isMobileSubtitle
    );

    const currentTitle = this.getCardTitle(row);
    const currentSubtitle = this.getCardSubtitle(row);

    filteredFields = filteredFields.filter((col) => {
      if (!col.prop) return true;

      const columnValue = String(this.getNestedValue(row, col.prop) || '');

      return columnValue !== currentTitle && columnValue !== currentSubtitle;
    });

    return filteredFields;
  }

  private filterFirstColumnIfUsedAsTitle(
    fields: Partial<NgxDatatableColumn>[],
    row: any
  ): Partial<NgxDatatableColumn>[] {
    if (this.cardTitleField || this.cardSubtitleField) {
      return fields;
    }

    const firstColumn = this.visibleColumns[0];
    if (
      firstColumn &&
      this.getCardTitle(row) ===
        this.displayString(this.getNestedValue(row, firstColumn.prop || ''))
    ) {
      return fields.slice(1);
    }

    return fields;
  }

  private applyFieldLimit(
    fields: Partial<NgxDatatableColumn>[],
    row: any
  ): Partial<NgxDatatableColumn>[] {
    return this.isCardExpanded(row)
      ? fields
      : fields.slice(0, this.maxVisibleFields);
  }

  public hasMoreFields(row: any): boolean {
    if (this.isCardExpanded(row)) {
      return false;
    }

    let fieldsToShow = [...this.visibleColumns];
    fieldsToShow = this.filterActionColumns(fieldsToShow);
    fieldsToShow = this.filterExpandableAndFrozenColumns(fieldsToShow);
    fieldsToShow = this.filterTitleAndSubtitleColumns(fieldsToShow, row);
    fieldsToShow = this.filterFirstColumnIfUsedAsTitle(fieldsToShow, row);

    if (fieldsToShow.length <= this.maxVisibleFields) {
      return false;
    }

    const additionalFields = fieldsToShow.slice(this.maxVisibleFields);

    return additionalFields.some((column) => {
      if (column.cellTemplate) {
        return !column.prop || this.isValidValue(this.getNestedValue(row, column.prop));
      }

      const fieldValue = this.getFieldValue(row, column);

      return (
        fieldValue !== 'N/A' &&
        fieldValue !== null &&
        fieldValue !== undefined &&
        fieldValue !== '' &&
        (typeof fieldValue !== 'string' || fieldValue.trim() !== '')
      );
    });
  }

  public changeLimit(event: Event): void {
    const limit = parseInt((event.target as HTMLSelectElement).value, 10);
    this.onChangeLimit.emit(limit);
  }

  public getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  public getDataDescription(): string {
    if (!this.config?.columns) return '';

    const visibleDataColumns = this.getVisibleDataColumnNames();
    return this.formatDataDescription(visibleDataColumns);
  }

  private getVisibleDataColumnNames(): string[] {
    return this.visibleColumns
      .filter((col) => this.isNotActionColumn(col))
      .filter((col) => col.name)
      .map((col) => col.name!)
      .slice(0, 3);
  }

  private isNotActionColumn(col: Partial<NgxDatatableColumn>): boolean {
    const actionColumns = this.actionColumns;
    return !actionColumns.some((actionCol) => actionCol.name === col.name);
  }

  private formatDataDescription(columnNames: string[]): string {
    const defaultDescription = this.translateService.instant('words.data');

    if (columnNames.length === 0) {
      return defaultDescription;
    }

    if (columnNames.length === 1) {
      return columnNames[0] || defaultDescription;
    }

    if (columnNames.length === 2) {
      return this.formatTwoColumns(columnNames);
    }

    return this.formatMultipleColumns(columnNames);
  }

  private formatTwoColumns(columnNames: string[]): string {
    return `${columnNames[0]} y ${columnNames[1]}`;
  }

  private formatMultipleColumns(columnNames: string[]): string {
    const lastField = columnNames.pop();
    return `${columnNames.join(', ')} y ${lastField}`;
  }

  public getExpandableContentType(row: any): 'array' | 'object' | 'none' {
    const arrayData = this.getExpandableArrayData(row);
    if (arrayData && arrayData.length > 0) {
      return 'array';
    }

    const objectData = this.getExpandableObjectData(row);
    if (objectData && typeof objectData === 'object') {
      return 'object';
    }

    return 'none';
  }

  public getExpandableArrayData(row: any): any[] | null {
    for (const [key, value] of Object.entries(row)) {
      if (Array.isArray(value) && value.length > 0) {
        return value;
      }
    }
    return null;
  }

  public getExpandableObjectData(row: any): any | null {
    for (const [key, value] of Object.entries(row)) {
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0
      ) {
        return value;
      }
    }
    return null;
  }

  public getItemTitle(item: any): string {
    return this.findFirstStringValue(item) || 'Item';
  }

  private findFirstStringValue(item: any): string | null {
    for (const [key, value] of Object.entries(item)) {
      const stringValue = this.extractStringFromItemValue(value);
      if (stringValue) {
        return stringValue;
      }
    }
    return null;
  }

  public getItemSubtitle(item: any): string | null {
    return this.findSecondStringValue(item);
  }

  private findSecondStringValue(item: any): string | null {
    let stringCount = 0;

    for (const [key, value] of Object.entries(item)) {
      const stringValue = this.extractStringFromItemValue(value);

      if (stringValue) {
        stringCount++;
        if (this.isSecondString(stringCount)) {
          return stringValue;
        }
      }
    }

    return null;
  }

  private extractStringFromItemValue(value: any): string | null {
    if (this.isValidStringValue(value)) {
      return value;
    }

    if (this.isLocalizableValueObject(value)) {
      return this.extractLocalizedStringValue(value);
    }

    return null;
  }

  private isValidStringValue(value: any): boolean {
    return value && typeof value === 'string' && value.trim().length > 0;
  }

  private isLocalizableValueObject(value: any): boolean {
    return value && typeof value === 'object' && !Array.isArray(value);
  }

  private extractLocalizedStringValue(value: any): string | null {
    const langValue = this.getValueByCurrentLocale(value);
    return langValue && typeof langValue === 'string' ? langValue : null;
  }

  private getValueByCurrentLocale(value: any): any {
    return (
      (value as any)[this.locale] ||
      (value as any)['en'] ||
      (value as any)['es']
    );
  }

  private isSecondString(count: number): boolean {
    return count === 2;
  }

  public getItemStatus(item: any): string | null {
    for (const [key, value] of Object.entries(item)) {
      if (value != null && value !== '') {
        return String(value);
      }
    }
    return null;
  }

  public getItemFields(
    item: any
  ): Array<{ label: string; value: string; important?: boolean }> {
    const fields: Array<{ label: string; value: string; important?: boolean }> =
      [];

    Object.keys(item).forEach((key) => {
      const field = this.processItemField(key, item[key]);
      if (field) {
        fields.push(field);
      }
    });

    return fields;
  }

  private processItemField(
    key: string,
    value: any
  ): { label: string; value: string; important?: boolean } | null {
    if (!this.isValidItemFieldValue(value)) {
      return null;
    }

    const processedValue = this.processItemFieldValue(value);
    const formattedValue = this.formatItemFieldValue(processedValue);

    return {
      label: this.formatFieldLabel(key),
      value: String(formattedValue),
      important: this.isNumericValue(processedValue),
    };
  }

  private isValidItemFieldValue(value: any): boolean {
    return value != null && value !== '';
  }

  private processItemFieldValue(value: any): any {
    if (this.isLocalizableValueObject(value)) {
      return this.extractLocalizedItemValue(value);
    }
    return value;
  }

  private extractLocalizedItemValue(value: any): any {
    const langValue = this.getValueByCurrentLocale(value);
    return langValue || String(value);
  }

  private formatItemFieldValue(value: any): any {
    if (this.isDateValue(value)) {
      return this.datePipe.transform(value, 'short') || value;
    }
    return value;
  }

  public getObjectFields(obj: any): Array<{ label: string; value: string }> {
    const fields: Array<{ label: string; value: string }> = [];

    Object.keys(obj).forEach((key) => {
      const processedField = this.processObjectField(key, obj[key]);
      if (processedField) {
        fields.push(processedField);
      }
    });

    return fields;
  }

  private processObjectField(
    key: string,
    value: any
  ): { label: string; value: string } | null {
    if (!this.isValidFieldValue(value)) {
      return null;
    }

    const processedValue = this.processFieldValue(value);

    return {
      label: this.formatFieldLabel(key),
      value: String(processedValue),
    };
  }

  private isValidFieldValue(value: any): boolean {
    return value != null && value !== '';
  }

  private processFieldValue(value: any): any {
    if (this.isLocalizableValueObject(value)) {
      return this.extractLocalizedObjectValue(value);
    }
    return value;
  }

  private extractLocalizedObjectValue(value: any): any {
    const langValue = this.getValueByCurrentLocale(value);
    return langValue || String(value);
  }

  private formatFieldLabel(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  private convertTablesToCards(): void {
    const offcanvasBody = document.querySelector(
      '#expandableOffcanvas .table-to-cards-wrapper'
    );

    if (!offcanvasBody) return;

    const tables = offcanvasBody.querySelectorAll('table');

    tables.forEach((table: HTMLTableElement) => {
      this.processTableForMobileCards(table);
    });
  }

  private processTableForMobileCards(table: HTMLTableElement): void {
    const headers: string[] = [];
    const headerRow = table.querySelector('thead tr');

    if (headerRow) {
      const headerCells = headerRow.querySelectorAll('th, td');
      headerCells.forEach((cell: Element) => {
        let headerText = cell.textContent?.trim() || '';

        headerText = headerText.replace(/\{\{.*?\}\}/g, '').trim();

        headers.push(headerText);
      });
    }

    const tbody = table.querySelector('tbody');
    if (tbody) {
      const rows = tbody.querySelectorAll('tr');

      rows.forEach((row: HTMLTableRowElement) => {
        const cells = row.querySelectorAll('td');

        cells.forEach((cell: HTMLTableCellElement, index: number) => {
          if (headers[index]) {
            cell.setAttribute('data-label', headers[index]);
          } else {
            const firstRowCells = tbody
              .querySelector('tr')
              ?.querySelectorAll('td');
            if (firstRowCells && firstRowCells[index]) {
              cell.setAttribute('data-label', `Campo ${index + 1}`);
            } else {
              cell.setAttribute('data-label', `Campo ${index + 1}`);
            }
          }
        });
      });
    }

    table.classList.add('processed-for-mobile');
  }
}
